// External (palette) drag: a card dragged from outside the grid previews as a
// phantom layout item and commits through `onExternalDrop` (the consumer
// creates the real instance). Only reachable when an ancestor
// `SortableGridDndScope` routes its DndContext events into this grid.
//
// Live sibling reflow is OPT-IN (hold Cmd/Alt — see use-reflow-modifier.ts).
// Without it the phantom marks the settled landing cell with siblings
// untouched, and the single authoritative reflow happens on drop.

import * as React from 'react';
import type {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
} from '@dnd-kit/core';

import { externalGridDragItem, subgridZoneOver } from './grid-dnd-scope';
import {
  externalDropCell,
  layoutBottom,
  moveItem,
  type GridLayoutItem,
} from './grid-layout';
import { activatorPoint } from './grid-geometry';
import { EXTERNAL_ID, type GridGestureCore } from './grid-gesture';
import { reflowModifierHeld, useReflowModifier } from './use-reflow-modifier';
import type { Omittable } from "../../lib/props";

const DEFAULT_EXTERNAL_SPAN = { w: 6, h: 4 };

export interface ExternalDragHandlers {
  onDragStart: (event: DragStartEvent) => void;
  onDragMove: (event: DragMoveEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;
}

export type ExternalDragOptions = Omittable<{
  onExternalDrop?: (
    item: unknown,
    cell: { x: number; y: number },
    siblingMoves: GridLayoutItem[],
  ) => void;
  externalSpan?: (item: unknown) => { w: number; h: number };
}>;

/** The full reflow a drop at `cell` produces — the live preview when the
 *  modifier is held, and the authoritative drop resolution always. */
function settleDrop(
  core: GridGestureCore,
  startLayout: GridLayoutItem[],
  span: { w: number; h: number },
  cell: { x: number; y: number },
): GridLayoutItem[] {
  const phantom: GridLayoutItem = {
    id: EXTERNAL_ID,
    x: cell.x,
    y: cell.y,
    w: span.w,
    h: span.h,
  };
  return moveItem(
    [...startLayout, phantom],
    EXTERNAL_ID,
    cell.x,
    cell.y,
    core.columns,
    core.flow,
  );
}

export function useExternalDrag(
  core: GridGestureCore,
  options: ExternalDragOptions,
): ExternalDragHandlers {
  // Stable-event pattern: handlers stay referentially stable across renders
  // and read the latest consumer callbacks through this ref.
  const optionsRef = React.useRef(options);
  React.useLayoutEffect(() => {
    optionsRef.current = options;
  });

  const reflowHeldRef = React.useRef(false);

  const applyPreview = React.useCallback(
    (cell: { x: number; y: number }) => {
      const scratch = core.scratch.current;
      const span = scratch.externalSpan;
      if (!span) return;
      const settled = settleDrop(core, scratch.startLayout, span, cell);
      if (reflowHeldRef.current) {
        core.setPreview(settled);
        return;
      }
      // Siblings stay put; the phantom shows the settled landing cell.
      const phantom = settled.find((i) => i.id === EXTERNAL_ID);
      core.setPreview(
        phantom ? [...scratch.startLayout, phantom] : settled,
      );
    },
    [core],
  );
  useReflowModifier(core, 'external', reflowHeldRef, applyPreview);

  const onDragStart = React.useCallback(
    (event: DragStartEvent) => {
      const { onExternalDrop, externalSpan } = optionsRef.current;
      if (!core.canManage || !onExternalDrop) return;
      // Gesture exclusivity: one gesture at a time per grid — a palette drag
      // must not replace an in-flight marquee/resize.
      if (core.gestureRef.current != null) return;
      const item = externalGridDragItem(event);
      const scratch = core.scratch.current;
      scratch.metrics = core.measure();
      scratch.startLayout = core.layoutRef.current;
      scratch.lastTarget = null;
      scratch.externalItem = item;
      scratch.externalSpan = externalSpan?.(item) ?? DEFAULT_EXTERNAL_SPAN;
      reflowHeldRef.current =
        core.liveReflow || reflowModifierHeld(event.activatorEvent);
      core.setGesture({
        id: EXTERNAL_ID,
        mode: 'external',
        origin: null,
        preview: null,
        area: null,
      });
    },
    [core],
  );

  const onDragMove = React.useCallback(
    (event: DragMoveEvent) => {
      if (core.gestureRef.current?.mode !== 'external') return;
      const scratch = core.scratch.current;
      // Symmetric preview rule: preview iff the drag's `over` zone matches
      // this grid's own zone identity (page grids preview while over no
      // enabled zone; nested grids only while over their own zone — which
      // also replaces geometric gating, since `externalDropCell`'s open-ended
      // bottom would let a nested grid keep previewing for pointers below its
      // container). Outside the band, clear the phantom so this grid stops
      // advertising a landing cell; re-entering re-establishes it — the
      // pointer math below is stateless.
      if (subgridZoneOver(event.over) !== core.zoneId) {
        if (scratch.lastTarget) {
          scratch.lastTarget = null;
          core.setPreview(null);
        }
        return;
      }
      const metrics = scratch.metrics;
      const span = scratch.externalSpan;
      const el = core.gridEl();
      if (!metrics || !span || !el) return;
      const origin = activatorPoint(event);
      if (!origin) return;
      const point = {
        x: origin.x + event.delta.x,
        y: origin.y + event.delta.y,
      };
      // Re-read the rect every move: the grid grows as the preview opens a
      // hole, and a stale rect would reject drops near the new bottom edge.
      const raw = externalDropCell(
        metrics,
        el.getBoundingClientRect(),
        point,
        span,
        core.columns,
      );
      // Clamp the row to the layout bottom: any point below the last row means
      // "append at the end", and an unclamped row would let auto-scroll grow
      // the grid (and its scroll range) without bound.
      const cell = raw
        ? { x: raw.x, y: Math.min(raw.y, layoutBottom(scratch.startLayout)) }
        : null;
      const last = scratch.lastTarget;
      if (!cell) {
        if (last) {
          scratch.lastTarget = null;
          core.setPreview(null);
        }
        return;
      }
      if (last && last.x === cell.x && last.y === cell.y) return;
      scratch.lastTarget = cell;
      applyPreview(cell);
    },
    [core, applyPreview],
  );

  const onDragEnd = React.useCallback((event: DragEndEvent) => {
    if (core.gestureRef.current?.mode !== 'external') return;
    const scratch = core.scratch.current;
    // Drop ownership: exactly ONE grid commits — the one whose zone identity
    // matches the drop's `over` zone (page grids own null-zone drops). Zones
    // sit geometrically inside the page grid's band, so WITHOUT this guard a
    // zone drop would also fire the page grid's `onExternalDrop` — a double
    // insert.
    if (subgridZoneOver(event.over) !== core.zoneId) {
      scratch.externalItem = undefined;
      scratch.externalSpan = null;
      core.cancelGesture();
      return;
    }
    const item = scratch.externalItem;
    const span = scratch.externalSpan;
    const cell = scratch.lastTarget;
    const startLayout = scratch.startLayout;
    scratch.externalItem = undefined;
    scratch.externalSpan = null;
    // The drop commits via onExternalDrop (the consumer inserts the instance),
    // never via onLayoutChange — clear without committing.
    core.cancelGesture();
    // No target cell means the pointer never settled over the grid — treat
    // the drop as a cancel.
    const { onExternalDrop } = optionsRef.current;
    if (!cell || !span || !onExternalDrop) return;
    // The single authoritative reflow: without the Cmd/Alt opt-in the live
    // preview left siblings untouched, so displacement resolves here; with it
    // this recomputes exactly what the preview showed (moveItem is pure).
    const settled = settleDrop(core, startLayout, span, cell);
    const phantom = settled.find((i) => i.id === EXTERNAL_ID);
    if (!phantom) return;
    const before = new Map(startLayout.map((i) => [i.id, i]));
    // Filter moves to ids still in the LIVE layout — the reflow was computed
    // from the gesture's startLayout, and a sibling deleted mid-drag must not
    // be resurrected by its move entry.
    const liveIds = new Set(core.layoutRef.current.map((i) => i.id));
    const siblingMoves = settled.filter((i) => {
      if (i.id === EXTERNAL_ID) return false;
      if (!liveIds.has(i.id)) return false;
      const prev = before.get(i.id);
      return (
        !prev ||
        prev.x !== i.x ||
        prev.y !== i.y ||
        prev.w !== i.w ||
        prev.h !== i.h
      );
    });
    onExternalDrop(item, { x: phantom.x, y: phantom.y }, siblingMoves);
  }, [core]);

  const onDragCancel = React.useCallback(() => {
    if (core.gestureRef.current?.mode !== 'external') return;
    const scratch = core.scratch.current;
    scratch.externalItem = undefined;
    scratch.externalSpan = null;
    core.cancelGesture();
  }, [core]);

  return React.useMemo(
    () => ({ onDragStart, onDragMove, onDragEnd, onDragCancel }),
    [onDragStart, onDragMove, onDragEnd, onDragCancel],
  );
}
